import { onlineManager } from '@tanstack/react-query'
import { useConversationsSections } from 'chat/conversations/queries/context'
import { useMentionsUnreadCount } from 'chat/messages/queries/mentions'
import { HomeStackParamList, Route } from 'home/stack/types'
import { useCallback, useMemo } from 'react'
import { useUser } from 'users/queries/detail'

export function useRoutesMap(): Route[] {
  const { data: user } = useUser()
  const conversationsSections = useConversationsSections()
  const areUnreadMessages = conversationsSections.some(c => c.unread > 0)
  const dmUnreadCount =
    conversationsSections.find(c => c.type === 'direct')?.unread ?? 0
  const mentionUnreadCount = useMentionsUnreadCount()
  const isOffline = !onlineManager.isOnline()
  const personalUnreadCount = dmUnreadCount + mentionUnreadCount
  let conversationsBadgeCount: number | string | undefined

  if (personalUnreadCount > 0) {
    conversationsBadgeCount = personalUnreadCount
  } else if (areUnreadMessages) {
    conversationsBadgeCount = ''
  }

  const getRoute = useCallback(
    (name: keyof HomeStackParamList): Route => {
      switch (name) {
        case 'conversations':
          return {
            name,
            badgeValue: conversationsBadgeCount,
            disabled: isOffline,
          }
        case 'tools':
          return { name, disabled: isOffline }
        case 'account':
          return { name, disabled: isOffline }
        default:
          return { name }
      }
    },
    [conversationsBadgeCount, isOffline]
  )

  const routesMap: Route[] = useMemo(() => {
    const routes: Route[] = [getRoute('account')]
    if (user?.permissions) {
      const {
        canSeeMessages,
        canSeeNextShifts,
        canSeeHelperSchedule,
        canSeeStoreSchedule,
        canSeeTools,
      } = user.permissions
      if (canSeeMessages) {
        routes.unshift(getRoute('conversations'))
      }
      if (canSeeTools) {
        routes.unshift(getRoute('tools'))
      } else if (canSeeNextShifts) {
        routes.unshift(getRoute('open_shifts'))
      }
      if (canSeeHelperSchedule || canSeeStoreSchedule) {
        routes.unshift(getRoute('schedule'))
      }
    }
    return routes
  }, [getRoute, user?.permissions])

  return routesMap
}

export default useRoutesMap
